import React, { useEffect, useState } from "react";

import { keyBy, sortBy } from "lodash";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  Button,
  CheckboxGroupInput,
  FunctionField,
  TextInput,
  required,
} from "react-admin";

import { CustomFieldsDisplay } from "../../utils/constants";
import {
  AddAllIcon,
  CancelIcon,
  CloseIcon,
  CustomFieldsIcon,
} from "./iconography";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    paddingTop: "5px",
    paddingBottom: "5px",
    justifyContent: "space-between",
  },
  dialogContent: {
    // https://stackoverflow.com/questions/47698037/how-can-i-set-a-height-to-a-dialog-in-material-ui
    height: "90vh",
  },
  hidden: { display: "none" },
}));

const token = "||";

const getCustomFieldEncodedValue = (encoded) => {
  if (!encoded || typeof encoded !== "string") return "";
  const parts = encoded.split(token);
  if (parts.length < 2) return "";
  // Preserve any accidental extra tokens in the value
  return parts.slice(1).join(token);
};

const validateCustomFieldRequired = (value) =>
  required()(getCustomFieldEncodedValue(value));

export const parseCustomField = (field) => {
  if (field && typeof field === "string") {
    const values = field.split(token);
    return { name: values[0], value: values[1] };
  }
  return {};
};

export const parseCustomFieldToObject = (field) => {
  if (field && typeof field === "string") {
    const [key, value] = field.split(token);
    return { [key.trim()]: value };
  }
  return {};
};

export const parseCustomFieldId = (field) => {
  const parsedField = parseCustomField(field);
  if (parsedField?.name) return getCustomFieldId(parsedField.name);
  else return "";
};

export const parseCustomFieldName = (field) => {
  const parsedField = parseCustomField(field);
  if (parsedField?.name) return parsedField.name;
  else return "";
};

export const parseCustomFieldValue = (field) => {
  if (!field) return "";
  const parsedField = parseCustomField(field);
  if (parsedField?.name) return parsedField.value;
  else return "";
};

const getSourcePath = (source) => {
  const parts = source.split(".customFields");
  // Check if there is a prefix before 'customFields'
  if (parts.length > 1) {
    // Return the prefix
    return parts[0];
  }
  // If no prefix, return an empty string or null
  return null;
};

export const CustomInput = ({
  field,
  source,
  getSource,
  target,
  disabled,
  variant,
  ...props
}) => {
  const finalSource = getSource
    ? getSource(`${source}[${field.index}]`)
    : `${source}[${field.index}]`;

  if (!field) return null;
  if (target === CustomFieldsDisplay.MOBILE)
    return (
      <Typography variant={variant}>
        {" "}
        {field.name}: {field.value}{" "}
      </Typography>
    );
  return (
    <TextInput
      source={finalSource}
      label={field.name || "Custom Field"}
      variant="outlined"
      fullWidth
      parse={(v) => (v ? `${field.name}${token}${v}` : `${field.name}${token}`)}
      format={(v) => (v ? v.split(token).pop() : "")}
      initialValue={`${field.name}${token}`}
      disabled={disabled}
      required={!!field?.required}
      validate={
        !disabled && field?.required && field?.visible
          ? validateCustomFieldRequired
          : undefined
      }
      helperText={false}
      InputLabelProps={{ shrink: true }}
    />
  );
};

export const CustomField = ({ field, source, record, ...props }) => {
  const sourcePath = getSourcePath(source);
  const sourceRecord = sourcePath ? record?.[sourcePath] : record;
  if (!field || !sourceRecord?.customFields) return <></>;

  const fieldIndex = sourceRecord.customFields.findIndex(
    (itm) => parseCustomFieldId(itm) === field.id,
  );
  if (fieldIndex === -1) return <></>;

  const finalSource = `${
    sourcePath ? `${sourcePath}.` : ""
  }customFields[${fieldIndex}]`;
  const values = parseCustomField(sourceRecord.customFields[fieldIndex]);

  return (
    <FunctionField
      source={finalSource}
      label={field.name || "Custom Field"}
      fullWidth
      sortable={false}
      render={() => `${values?.value || ""}`}
    />
  );
};

export const getCustomFieldId = (field) => {
  let fieldName = field;
  if (typeof field === "object") {
    fieldName = field.name;
  }
  return fieldName
    ? fieldName
        .toLowerCase()
        .split(" ")
        .map((s, i) =>
          i === 0 ? s : s.charAt(0).toUpperCase() + s.substring(1),
        )
        .join("")
    : "";
};

export const getCustomFieldIdCleansed = (field) => {
  let fieldId = getCustomFieldId(field);
  return fieldId.replace(/[^a-zA-Z0-9 _-]/g, "");
};

export const getCustomFieldById = (recordFields, fieldId) => {
  if (recordFields) {
    const fieldById = recordFields.find((field) => {
      const parsedField = parseCustomField(field);
      const parsedFieldId = getCustomFieldId(parsedField);
      return parsedFieldId === fieldId;
    });
    return parseCustomField(fieldById);
  }
  return null;
};

export const getCustomFieldsToSave = (recordFields, accountFields) => {
  const allFields = sortBy(
    mergeFields(recordFields || [], accountFields || []),
    "index",
  );
  const toSaveFields = allFields.map((field) =>
    field.value
      ? `${field.name}${token}${field.value}`
      : `${field.name}${token}`,
  );

  if (toSaveFields?.length > 0) return toSaveFields;
  else return undefined;
};

const mergeFields = (recordFields, accountFields, target) => {
  let allFields = [];

  if (recordFields?.length > 0) {
    const fieldMap = keyBy(accountFields, "id");
    let index = 0;
    recordFields.forEach((field, idx) => {
      const values = parseCustomField(field);
      const fieldId = getCustomFieldId(values.name);
      const newField = {
        id: fieldId,
        name: values.name,
        value: values.value,
        index: idx,
        display: fieldMap[fieldId]?.display ? fieldMap[fieldId].display : null,
        required: fieldMap[fieldId]?.required ?? false,
        visible: true,
      };
      index = idx;
      allFields.push(newField);
    });

    let parsedFieldMap = keyBy(allFields, "id");
    accountFields.forEach((field) => {
      if (!parsedFieldMap[field.id])
        allFields.push({ ...field, index: ++index, visible: true });
    });
  } else
    allFields = accountFields.map((field, idx) => {
      return { ...field, index: idx, visible: true };
    });

  // hide fields marked not to display in "target" && empty fields for target === MOBILE
  if (target) {
    allFields = allFields.map((field) => ({
      ...field,
      visible:
        !field.display ||
        (field.display.includes(target) &&
          !(target === CustomFieldsDisplay.MOBILE && !field.value)),
    }));
  }

  return sortBy(allFields, "name");
};

export const CustomFields = ({
  recordFields,
  accountFields,
  source = "customFields",
  getSource,
  target,
  showHeader = true,
  inline = false,
  width,
  disabled,
  ...props
}) => {
  const classes = useStyles();
  const allFields = mergeFields(
    recordFields || [],
    accountFields || [],
    target,
  );

  if (!allFields || allFields.length === 0) return null;
  if (inline) {
    return allFields.map((field) => (
      <Grid
        key={field.name}
        item
        xs={12}
        md={width}
        className={field.visible ? "" : classes.hidden}
      >
        <CustomInput
          {...props}
          field={field}
          source={source}
          getSource={getSource}
          target={target}
          disabled={disabled}
        />
      </Grid>
    ));
  } else {
    return (
      <Grid container spacing={1}>
        {showHeader && (
          <Grid item xs={12}>
            <Typography variant="overline" color="textSecondary" gutterBottom>
              CUSTOM FIELDS
            </Typography>
          </Grid>
        )}
        {allFields.map((field) => (
          <Grid
            key={field.name}
            item
            xs={12}
            md={width}
            className={field.visible ? "" : classes.hidden}
          >
            <CustomInput
              {...props}
              field={field}
              source={source}
              getSource={getSource}
              target={target}
              disabled={disabled}
            />
          </Grid>
        ))}
      </Grid>
    );
  }
};

// display view only Typography.
export const CustomFieldsMobile = ({
  recordFields,
  accountFields,
  width,
  variant = "body1",
  ...props
}) => (
  <CustomFields
    {...props}
    recordFields={recordFields}
    accountFields={accountFields}
    target={CustomFieldsDisplay.MOBILE}
    width={width}
    showHeader={false}
    variant={variant}
  />
);

// copy product fields to recordFields
// display all reocrdFields
// display empty accountFileds where display contains TALLYSHEET
export const CustomFieldsTallySheet = (props) => (
  <CustomFields
    {...props}
    target={CustomFieldsDisplay.TALLYSHEET}
    showHeader={false}
    inline={true}
  />
);

// Select CustomFields to hide / show
export const CustomFieldsButton = ({
  form,
  accountFields,
  selectedCustomFields,
  setSelectedFields,
  ...props
}) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState();

  useEffect(() => {
    if (!selected && accountFields) {
      debugger;
      let mergeFields = selectedCustomFields
        ? [...accountFields, ...selectedCustomFields]
        : accountFields;
      const initSelection = mergeFields.reduce(
        (all, f) => (
          f.display.includes(CustomFieldsDisplay.TALLYSHEET) && all.push(f.id),
          all
        ),
        [],
      );
      setSelected(initSelection);
    }
  }, [accountFields]);

  const handleCheckboxChange = (value) => {
    debugger;
    setSelected(value);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleOpenFields = () => {
    setIsOpen(true);
  };

  const handleSetSelected = () => {
    debugger;
    const selectedFields = accountFields.map((field) =>
      selected.includes(field.id)
        ? { ...field, display: [CustomFieldsDisplay.TALLYSHEET] }
        : { ...field, display: [] },
    );

    setSelectedFields(selectedFields);
    form.change("selectedCustomFields", selectedFields);
    setIsOpen(false);
  };

  return (
    accountFields?.length > 0 && (
      <>
        <Button
          label="Custom Fields"
          color="default"
          onClick={handleOpenFields}
        >
          <CustomFieldsIcon />
        </Button>
        <Dialog maxWidth="sm" open={isOpen} fullWidth>
          <DialogTitle disableTypography className={classes.dialogTitle}>
            <h3>Select Custom Fields</h3>
            <IconButton aria-label="close" onClick={handleClose}>
              <CloseIcon fontSize="large" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers className={classes.dialogContent}>
            <CheckboxGroupInput
              source="customFieldSelect"
              label={false}
              row={false}
              fullWidth
              defaultValue={selected}
              choices={accountFields || []}
              onChange={handleCheckboxChange}
            />
          </DialogContent>
          <DialogActions>
            <Button
              label="Cancel"
              color="defalut"
              autoFocus
              onClick={handleClose}
            >
              <CancelIcon />
            </Button>
            <Button
              label="Set Selected"
              color="primary"
              onClick={handleSetSelected}
            >
              <AddAllIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  );
};

export const getCustomFieldsTable = (
  accountCustomFields = [],
  recordCustomFields = [],
) => {
  if (!accountCustomFields?.length > 0) return <></>;
  const customFields = recordCustomFields || [];

  const customFieldChunks = [];
  for (let i = 0; i < accountCustomFields.length; i += 3) {
    customFieldChunks.push(accountCustomFields.slice(i, i + 3));
  }

  const renderCustomFieldTable = (chunk, index) => (
    <Table
      key={"custom_fields_" + index}
      style={{ width: "100%", marginTop: index > 0 ? "10px" : "0" }}
    >
      <TableHead>
        <TableRow>
          {chunk.map((field, idx) => (
            <TableCell
              key={idx}
              style={{ textAlign: "center", width: "33%", padding: "3px" }}
            >
              {field.name}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          {chunk.map((field, idx) => (
            <TableCell
              key={idx}
              style={{ textAlign: "center", padding: "3px" }}
            >
              {parseCustomFieldValue(
                customFields.find((cf) => cf.startsWith(field.name)),
              )}
            </TableCell>
          ))}
        </TableRow>
      </TableBody>
    </Table>
  );

  return customFieldChunks.map((chunk, index) =>
    renderCustomFieldTable(chunk, index),
  );
};
